import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import HamburgerMenu from './HamburgerMenu';
import { cn } from '../../lib/utils';
import { useConnectivity } from '../../hooks/useConnectivity';

interface HeaderProps {
  // 'guard' shows the store label; 'admin' enables the demo toggle (when
  // demoMode/onToggleDemo are passed); 'civil' shows neither — used by
  // civil respondents.
  view: 'guard' | 'admin' | 'civil';
  demoMode?: boolean;
  onToggleDemo?: () => void;
  isSoundEnabled?: boolean;
  onToggleSound?: () => void;
  alertActive?: boolean;
}

export default function Header({ view, demoMode, onToggleDemo, alertActive }: HeaderProps) {
  const { t } = useTranslation();
  const { isOnline } = useConnectivity();

  return (
    <header
      className={cn(
        "sticky top-0 z-40 w-full border-b backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-colors duration-300",
        alertActive
          ? "bg-red-600 text-white border-red-700"
          : "bg-background/95 border-border"
      )}
    >
      <div className="flex h-14 items-center justify-between px-4 sm:px-6 max-w-screen-2xl mx-auto">
        {/* Logo + Brand. For civil respondents, link back to the entry page. */}
        <Link
          to={view === 'civil' ? '/civil-respondent' : '/guard'}
          className="flex items-center gap-2 flex-shrink-0"
        >
          <div
            className={cn(
              "flex items-center gap-2",
              alertActive && [
                "dark:text-white text-gray-900",
                "[&>button]:hover:!bg-red-700/20"
              ]
            )}
          >
            <img
              src="/logo.png"
              alt="iSentinel360"
              className="h-8 w-8 sm:h-9 sm:w-9"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="font-bold text-sm sm:text-lg">iSentinel360</span>
          </div>
        </Link>

        {/* Right side controls: theme, language, hamburger trigger. */}
        <div
          className={cn(
            "flex items-center gap-1 sm:gap-2 overflow-visible",
            alertActive && [
              "dark:text-white text-gray-900",
              "[&>span]:dark:text-white [&>span]:text-gray-900",
              "[&>button]:hover:!bg-red-700/20"
            ]
          )}
        >
          {/* Demo toggle — only on admin view, and only if both props are passed. */}
          {view === 'admin' && demoMode !== undefined && onToggleDemo && (
            <div className="hidden sm:flex items-center gap-2 mr-2">
              <span className="text-sm">{t('admin.demoMode')}</span>
              <button
                onClick={onToggleDemo}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  demoMode ? 'bg-primary' : 'bg-gray-300'
                )}
                aria-label={t('admin.demoMode')}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    demoMode ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            </div>
          )}

          {/* Store label — only on guard view (the "you are at store X" indicator). */}
          {view === 'guard' && (
            <span className="text-xs sm:text-sm text-muted-foreground mr-1 sm:mr-2 hidden xs:inline">
              {t('guard.store')}
            </span>
          )}

          <ThemeSwitcher />

          {/* v0.4.1: Language switcher is greyed out while offline.
              Changing language requires loading a new i18n file from
              the network, which doesn't work offline. The exception
              is the offline indicator itself — its strings are bundled
              in offlineStrings.ts (14 languages), so the user can
              change language there. But the LanguageSwitcher is shared
              across all pages, so for now we disable it globally when
              offline. A future improvement could be to keep the
              LanguageSwitcher enabled only on the offline indicator. */}
          <div
            className={cn(
              "transition-opacity",
              !isOnline && "opacity-50 pointer-events-none"
            )}
            title={!isOnline ? (t('common.offlineLanguageDisabled') || 'Language unavailable while offline') : undefined}
          >
            <LanguageSwitcher />
          </div>

          {/* v0.4.1: Hamburger menu is disabled while offline. The
              button is greyed out and can't be opened — the rest of
              the page (Header, NavBar, content) stays visible, but
              the user understands navigation is paused. */}
          <HamburgerMenu disabled={!isOnline} />
        </div>
      </div>
    </header>
  );
}
